import { I18n } from "@assets/strings";
import { useObservable } from "micro-observables";
import React, { JSX } from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { Description } from "@ui/components/containers/description";
import { PrimaryBold } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { ArrowIcon, PlusIconRound, RemoveIconRound } from "@ui/components/icons";
import { InfoBulle } from "@ui/components/infoBulle";
import { row, stack } from "@ui/components/layout";
import { LinkExternal } from "@ui/components/linkExternal";
import { FileUpload } from "@ui/components/mediaEditor/fileUpload";
import { theme } from "@ui/styles/theme";
import { alpha, mobile } from "@ui/styles/utils";
import { useApplicationEditorController } from "@ui/pages/application/applicationEditorPage/applicationEditorController/applicationEditorControllerContext";
import { FileReminderInfo } from "@ui/pages/application/applicationEditorPage/fileReminderInfo";
import { videoInfos } from "./videoInfos";

const url_video_challenge = "https://www.youtube.com/watch?v=LUzZJazBZRY&list=PLA-YdjDAbABi6JwjxnC2vdMXunr9Z5VK4";

export const VideoTeasingSectionEditor: React.FC = ({ ...htmlProps }) => {
	const controller = useApplicationEditorController();
	const { videoId } = useObservable(controller.application);

	const getFormItem = (field: "video_story"): JSX.Element => (
		<InfoEdition>
			<PrimaryBold>
				{I18n.t(`application.form.${field}.label`)}
				<span style={{ color: "red" }}>*</span>
			</PrimaryBold>
			<InfoBulle text={I18n.t(`application.form.${field}.tooltip`)} />
		</InfoEdition>
	);

	return (
		<Container {...htmlProps}>
			<VideoInfoBloc>
				{videoInfos.map((section) => (
					<React.Fragment key={section.title}>
						<Title>{I18n.t(`application.form.video_story.infos.${section.title}.title`)}</Title>
						<ul style={{ margin: "10px 30px" }}>
							{section.infos.map((info) => (
								<li key={info} style={{ marginBottom: "15px" }}>
									<Description>{I18n.t(`application.form.video_story.infos.${section.title}.${info}`)}</Description>
								</li>
							))}
						</ul>
					</React.Fragment>
				))}
			</VideoInfoBloc>
			<LinkExternal to={url_video_challenge}>
				<Info>
					{I18n.t("application.form.video_sample.label")} <ArrowIcon color={theme.colors.secondary} />
				</Info>
			</LinkExternal>

			<FormField>
				{getFormItem("video_story")}
				<FileReminderInfo fileSize={250} acceptedFormat="video" />
				<StyledFileUplaod type="video" onUpload={(media) => controller.setVideoTeasing(media)}>
					{(_, loading) => (
						<div className="fileUpload">
							<StyledPlaceholder>{I18n.t("application.form.video_story.placeholder")}</StyledPlaceholder>
							{loading ? <ClipLoader size={16} /> : <PlusIconRound color={theme.colors.secondary} />}
						</div>
					)}
				</StyledFileUplaod>
				{videoId && (
					<StyledFilesContainer>
						<StyledFileInfo>
							{videoId?.displayName}
							<RemoveIconRound onClick={() => controller.removeVideoTeasing()} />
						</StyledFileInfo>
					</StyledFilesContainer>
				)}
			</FormField>
		</Container>
	);
};

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;

	margin-top: 60px;
	@media ${mobile} {
		margin-top: 80px;
	}
`;

const Info = styled.div`
	display: flex;
	align-items: center;
	gap: 5px;
	${({ theme }) => theme.textStyles.description};
	color: ${theme.colors.secondary} !important;
	font-size: 16px !important;
	line-height: 25px;
	width: 87%;

	> svg {
		transform: rotate(180deg);
	}
`;

const InfoEdition = styled.div`
	${row(10, "flex-start", "stretch")};
`;

const FormField = styled.div`
	${stack(10, "flex-start", "stretch")};
	width: 66%;
	@media ${mobile} {
		width: 100%;
	}
`;

const StyledFileUplaod = styled(FileUpload)`
	.fileUpload {
		display: flex;
		align-items: center;
		border-radius: 7px;
		align-content: flex-end;
		justify-content: space-between;
		padding: 11px 15px;
		background-color: "rgba(" #f3f4f6 ",0.4)";
		background-color: rgba(243, 244, 246, 0.4);
		border: 1.5px solid #e9e9e9;
	}
`;

const StyledFilesContainer = styled.div`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	grid-gap: 20px;
`;

const StyledFileInfo = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	background-color: ${alpha("#BEDAFF", 0.5)};
	padding: 10px;
	font-size: 14px;
	border-radius: 7px;
	gap: 10px;
`;

const StyledPlaceholder = styled.div`
	color: #8b8b8b;
	font-size: 14px;
	font-weight: 500;
`;

const VideoInfoBloc = styled(RoundedBlock)`
	border: 1px solid #e9e9e9;
	padding: 15px;
	width: 80%;

	@media ${mobile} {
		width: 100%;
	}
`;

const Title = styled.div`
	color: ${theme.colors.secondary};
`;
